import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminLoginResult, AdminUser } from '@pmc/shared';
import { api, getToken, setToken } from './api';

interface AuthContextValue {
  user: AdminUser | null;
  isLoading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => Boolean(getToken()));

  // The token is only trusted once the server confirms it — a stale token in
  // sessionStorage must not render the admin shell.
  const { data, isLoading } = useQuery({
    queryKey: ['admin-me'],
    queryFn: () => api.get<AdminUser>('/admin/me'),
    enabled: hasToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const signIn = useCallback(
    async (username: string, password: string) => {
      const result = await api.post<AdminLoginResult>('/admin/login', { username, password });
      setToken(result.token);
      setHasToken(true);
      queryClient.setQueryData(['admin-me'], result.user);
    },
    [queryClient],
  );

  const signOut = useCallback(() => {
    setToken(null);
    setHasToken(false);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: hasToken ? (data ?? null) : null,
      isLoading: hasToken && isLoading,
      signIn,
      signOut,
    }),
    [hasToken, data, isLoading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
